import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants/query-keys';
import { useInfiniteList } from '@/hooks/useInfiniteList';

import {
  type ConvertInput,
  convertProspect,
  createProspect,
  listProspects,
  type ProspectFilter,
  updateProspect,
} from '../api/prospects.api';
import type { ProspectInput } from '../schemas/prospect.schema';

/** Enquiries loaded as the user scrolls a list inside a card (the dashboard's follow-ups). */
export function useProspectFeed(filter: Omit<ProspectFilter, 'page' | 'pageSize'>, enabled = true) {
  return useInfiniteList({
    queryKey: QUERY_KEYS.prospects.feed({ ...filter }),
    fetchPage: (page, signal) => listProspects({ ...filter, page, pageSize: 10 }, signal),
    enabled,
  });
}

export function useProspects(filter: ProspectFilter, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.prospects.list({ ...filter }),
    queryFn: ({ signal }) => listProspects(filter, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useSaveProspect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: ProspectInput }) =>
      id === null ? createProspect(input) : updateProspect(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.prospects.all }),
  });
}

export function useConvertProspect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ConvertInput }) => convertProspect(id, input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.prospects.all }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members.all }),
      ]);
    },
  });
}
